import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useUnits } from "../lib/useUnits";
import { litres } from "../lib/volume";
import { useToast } from "../lib/toast";
import { hapticTick } from "../lib/haptics";

/** Ticks that rise with the water (fill ~500ms) or fall as it drains —
    the vibration follows the animation instead of one tap at press. */
function fillBuzz() {
  [0, 150, 300, 450].forEach((ms) => setTimeout(hapticTick, ms));
}
function emptyBuzz() {
  [0, 160].forEach((ms) => setTimeout(hapticTick, ms));
}
import type { Drink } from "@shared/types";

const GLASS_ML = 250;
const MIN_GLASSES = 4;

type Props = {
  consumedMl: number;
  targetMl: number;
  drinks: Drink[];
  selectedDate: string;
  onChange: () => void | Promise<void>;
};

export function WaterTracker({ consumedMl, targetMl, drinks, selectedDate, onChange }: Props) {
  const t = useT();
  const toast = useToast();
  const units = useUnits();
  const imperial = units.prefs.serving === "imperial";
  const [busy, setBusy] = useState(false);

  const filled = Math.floor(consumedMl / GLASS_ML);
  const visible = Math.max(MIN_GLASSES, filled + 1);

  async function addGlass() {
    if (busy) return;
    fillBuzz(); // the vibration rises with the water
    setBusy(true);
    try {
      await api.addDrink({
        type: "water",
        volume_ml: GLASS_ML,
        kcal: 0,
        note: null,
        consumed_on: selectedDate,
        consumed_at: Math.floor(Date.now() / 1000),
      });
      await onChange();
    } catch {
      toast.error(t("toast.failed"));
    } finally {
      setBusy(false);
    }
  }

  async function removeGlass() {
    if (busy) return;
    emptyBuzz();
    const latest = drinks
      .filter((d) => d.type === "water" && d.volume_ml === GLASS_ML)
      .sort((a, b) => b.consumed_at - a.consumed_at)[0];
    if (!latest) return;
    setBusy(true);
    try {
      await api.deleteDrink(latest.id);
      await onChange();
    } catch {
      toast.error(t("toast.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="glass rounded-2xl p-4 space-y-3">
      {/* − amount + : mirrors the weight card so the two daily "how much"
          cards read the same. The centred figure is what you're changing;
          the − / + glasses sit under your thumbs. */}
      <div className="flex items-center gap-3">
        <button
          onClick={removeGlass}
          disabled={busy || filled === 0}
          aria-label={t("water.remove")}
          className="w-14 h-14 shrink-0 rounded-full bg-surface-strong text-2xl leading-none press disabled:opacity-40"
        >
          −
        </button>

        <div className="flex-1 min-w-0 text-center">
          <div className="text-2xl font-bold tabular-nums">
            {imperial ? units.volume(consumedMl).value : litres(consumedMl)}{" "}
            <span className="text-sm text-fg-muted font-medium">{imperial ? "fl oz" : "L"}</span>
          </div>
          <div className="text-sm text-fg-muted mt-0.5">
            {t("water.of_target", { litres: imperial ? `${units.volume(targetMl).value} fl oz` : litres(targetMl) })}
          </div>
        </div>

        <button
          onClick={addGlass}
          disabled={busy}
          aria-label={t("water.add")}
          className="w-14 h-14 shrink-0 rounded-full bg-surface-strong text-2xl leading-none press disabled:opacity-40"
        >
          +
        </button>
      </div>

      <div className="flex gap-3 flex-wrap justify-center">
        {Array.from({ length: visible }).map((_, i) => {
          const isFilled = i < filled;
          return (
            <Glass
              key={i}
              filled={isFilled}
              disabled={busy}
              onClick={isFilled ? removeGlass : addGlass}
            />
          );
        })}
      </div>
    </section>
  );
}

function Glass({ filled, onClick, disabled }: { filled: boolean; onClick: () => void; disabled: boolean }) {
  // True only for the length of the fill transition. Motion marks the moment
  // the water arrives, then the surface settles — an endlessly moving glass is
  // decoration, and the motion budget (UI plan §6.1) says decoration gets cut.
  const [filling, setFilling] = useState(false);
  const wasFilled = useRef(filled);
  useEffect(() => {
    if (filled && !wasFilled.current) {
      setFilling(true);
      const id = setTimeout(() => setFilling(false), 900);
      wasFilled.current = filled;
      return () => clearTimeout(id);
    }
    wasFilled.current = filled;
  }, [filled]);

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={filled ? "remove glass" : "add glass"}
      className={[
        "relative w-11 h-12 rounded-b-xl rounded-t-md overflow-hidden",
        "border-2 active:scale-95 disabled:opacity-50",
        "transition-[border-color,transform] duration-200",
        filled ? "border-accent-move" : "border-line bg-surface",
      ].join(" ")}
    >
      {/* Animated water fill — uses a GPU transform (scaleY) rather than height
          so it composites on its own layer and doesn't force the glass card's
          backdrop-filter to re-rasterize every frame (which made it janky). */}
      <div
        aria-hidden
        className="absolute inset-0 origin-bottom transition-transform duration-500 ease-out"
        style={{ transform: filled ? "scaleY(1)" : "scaleY(0)" }}
      >
        <div className="absolute inset-x-0 bottom-0 top-1.5 bg-accent-move/90" />
        {/* The surface. Two offset sine crests drifting in opposite directions
            read as water rather than as a rising rectangle; the seam never
            lands in the same place twice because the periods differ. Pure
            transform, so it stays on the compositor, and it stops entirely
            under prefers-reduced-motion (index.css). */}
        <svg
          className="absolute inset-x-0 top-0 h-3 w-full overflow-visible"
          viewBox="0 0 120 12"
          preserveAspectRatio="none"
          style={{ color: "rgba(255,255,255,0.85)" }}
        >
          <path className={`water-wave water-wave--front ${filling ? "is-filling" : ""}`} d="M0 6 Q15 0 30 6 T60 6 T90 6 T120 6 T150 6 T180 6 V12 H0 Z" fill="currentColor" />
          <path className={`water-wave water-wave--back ${filling ? "is-filling" : ""}`} d="M0 7 Q15 2 30 7 T60 7 T90 7 T120 7 T150 7 T180 7 V12 H0 Z" fill="currentColor" opacity="0.5" />
        </svg>
      </div>
      {/* Drop emoji fades in after the water has mostly risen */}
      <span
        aria-hidden
        className={[
          "absolute inset-0 flex items-center justify-center text-base",
          "transition-opacity duration-200",
          filled ? "opacity-100 delay-300" : "opacity-0",
        ].join(" ")}
      >
        💧
      </span>
    </button>
  );
}
